/**
 * Custom product orchestrator v2 — one file, sectioned.
 *
 * Patterns (in-file):
 *   Facade   — CustomProductOrchestrator public API
 *   Table    — ASSET_DEFS + boot input/form (strategy per product)
 *   Adapters — ports catalog (form, engine, S3, history, upload, panel)
 *   Command  — startFromUpload|History|Share|Props → single start()
 *   Pipeline — generation + phase; inbound onMockup*|onS3*|onPropChange
 *   Config   — buildConfig + createConfig at init; Pipeline reads `config`
 *   DOM — custom-product-panel-ui-v2 (via adapters.panel; required at init)
 *
 * Public: boot | startFrom* | getConfig | getImageTask | getMockupProps |
 *   matchesUploadTarget | initialState
 *
 * ── Boot example (shaped-v2) — snippets/custom-product-boot-shaped-v2.liquid ──
 *   CustomProductOrchestrator.boot({
 *     formId, productType, sizePreview, history, share, analytics,
 *     input: { props, sources, save },
 *     form:  { [role]: { required } },   // roles ⊆ ASSET_DEFS
 *     getEngine: () => window.MyEngine   // or engine: window.MyEngine
 *   });
 *
 * ── startFromUpload example (full) ─────────────────────────────────────────
 *   CustomProductOrchestrator.startFromUpload({
 *     props: { color: 'pink' },           // or getMockupProps(); keys ⊆ input.props
 *     file: uploadFile,                   // optional File — id / history hash only
 *     engine: {
 *       sources: { main: media }          // dataUrl | http | File | { dataUrl }
 *       // back?: media                   // square dual-side
 *       // pillowData?: blob              // history/share restore (instead of sources)
 *     },
 *     save: {
 *       final: media                      // required — print → S3 → _Image_Upload
 *       // original?: media               // optional raw → _Image_original
 *     }
 *     // fileId?, fileOriginalId?         // history/share only — not for normal upload
 *   });
 *
 *   `final` = print file (always in boot form). `original` = raw upload when needed.
 *   `file` = uploaded File for task-id / history lookup only (not sent to engine).
 *   No fallback from sources/save — pass `file` explicitly when hash/history needed.
 *
 *   Live boots: custom-product-boot-shaped-v2.liquid | boot-square-v2.liquid | boot-towel-v2.js
 *
 * ── Mockup Engine contract ──────────────────────────────────────────────────
 * Required on engine object:
 *   id?       string
 *   target    string — upload data-target / viewer selector key (e.g. 'pillow-3d')
 *   getEl()   → Element | null
 *   abort()   → void
 *   process({ sources?, pillowData?, props?, generation? }) → any | Promise
 *     sources: { main?: dataUrl|http, back?: … }   // media strings
 *     pillowData: engine state blob (restore; not an image)
 *     props: { [mockupProp]: value }  // from input.props
 *     generation: number              // echo on events; stale jobs ignored
 *
 * Events (jQuery on document, fixed names — not configurable):
 *   mockup:start   → { source?: generate|restore|props, generation? }
 *   mockup:success → {
 *     mockups: { main: { noBg, withBg }, back?: { noBg, withBg } },
 *     pillowData?,                 // present on generate | restore | props (colorbg)
 *     source: generate|restore|props,  // used internally for mockup SKIP on restore
 *     generation?
 *   }
 *   mockup:error   → { type?, message?, generation? }
 *
 * customProduct:* detail — same keys on every event; unused = null.
 *
 *   {
 *     pipelineSource,  // upload | history | share | props
 *     generation,      // number
 *     imageTaskSlim,   // snapshot | null — see below
 *     props,           // form mockup props
 *     file,            // { name, size, type, lastModified } | null
 *     preview,         // usually null (use imageTaskSlim.preview)
 *     s3Url, url, urls,// usually null (s3 on slim / getImageTask)
 *     skipped,         // bool | null — saveSuccess (true = no upload)
 *     errorType, message,
 *     target,
 *     config           // ready only
 *   }
 *
 *   imageTaskSlim = {
 *     id, fileOriginalId, props, s3Url, preview,
 *     status: { mockup, saveToS3 },  // pending|success|error|skip
 *     error: { event, type, message } | null
 *   }
 *   // no pillowData | dataUrl | originalS3Url | previewWithBg
 *
 *   Typical non-null by event:
 *     ready         → config
 *     init          → pipelineSource, generation, file?, s3Url?
 *     propsInit     → pipelineSource:'props', generation, props, imageTaskSlim
 *     started       → pipelineSource, generation, imageTaskSlim, file?
 *     mockupStart   → defaults
 *     mockupSuccess → imageTaskSlim (fresh preview/status)
 *     saveStart     → defaults
 *     saveSuccess   → skipped, imageTaskSlim
 *     success       → imageTaskSlim (both tracks done)
 *     error         → errorType, imageTaskSlim?
 *
 *   Full live task (pillowData, …): getImageTask() — not in event detail.
 *   History on success reads getImageTask(), not imageTaskSlim.
 *
 *   Engine inbound mockup:success source restore → status.mockup SKIP (skipMockup).
 *   PillowData-only start (history hit / history UI): synthetic mockup SKIP after
 *   runEngineProcess — WC may no-op identical pillowdata and never emit success.
 *   props → save SKIP in propsStart.
 *   S3 = input roles only (start sync); result mockups → form, not S3.
 *   Analytics processType ← pipelineSource.
 *
 * Reference engines: window.ShapedPillowEngine | window.SquarePillowEngine | window.TowelMockupEngine
 *
 * Canon start: { props, file?, engine: { sources|pillowData }, save }
 * Sections: 1–3 config/media/formWrite+createConfig | 4 constants |
 *   5 adapters | 6 pipeline | 7 events | 8 boot/api
 */

(() => {
  'use strict';

  // ═══════════════════════════════════════════════════════════
  // 1) CONFIG — ASSET_DEFS, buildConfig, accessors (createConfig after validate)
  // ═══════════════════════════════════════════════════════════

  /**
   * Single role table — boot form keys must match.
   * key = cart property
   * kind = 'result' | 'input' (how/when we write form)
   * from = path | path[] on payload (first non-empty wins)
   * toHistory = counts toward historyOk gate (≤1 result + ≤1 input with toHistory:true)
   *   false = sidecar (still written to form; not a history restore key)
   */
  const ASSET_DEFS = {
    mockup: {
      key: '_Mockup',
      kind: 'result',
      from: ['mockups.main.withBg', 'mockups.main.noBg'],
      toHistory: true
    },
    mockupBack: {
      key: '_Mockup_for_side_2',
      kind: 'result',
      from: ['mockups.back.withBg', 'mockups.back.noBg'],
      toHistory: true
    },
    removebg: {
      key: '_removebg',
      kind: 'result',
      from: 'removebg.main',
      toHistory: false
    },
    final: {
      key: '_Image_Upload',
      kind: 'input',
      from: 'save.final',
      toHistory: true
    },
    finalBack: {
      key: '_Image_for_side_2',
      kind: 'input',
      from: 'save.finalBack',
      toHistory: true
    },
    original: {
      key: '_Image_original',
      kind: 'input',
      from: 'save.original',
      toHistory: true
    },
    originalBack: {
      key: '_Image_original2',
      kind: 'input',
      from: 'save.originalBack',
      toHistory: true
    }
  };

  const PRODUCT_BOOT_KEYS = [
    'formId',
    'productType',
    'sizePreview',
    'history',
    'share',
    'analytics',
    'input',
    'form'
  ];

  const requireNonEmptyString = (value, label) => {
    const trimmed = value == null ? '' : String(value).trim();
    if (!trimmed) {
      throw new Error(`init: ${label} is required (non-empty string)`);
    }
    return trimmed;
  };

  const requireBoolean = (value, label) => {
    if (typeof value !== 'boolean') {
      throw new Error(`init: ${label} is required (boolean)`);
    }
    return value;
  };

  /**
   * Product boot — every PRODUCT_BOOT_KEYS field required (boots pass full config).
   */
  const resolveProductBoot = (raw) => {
    for (const key of PRODUCT_BOOT_KEYS) {
      if (raw[key] === undefined || raw[key] === null) {
        throw new Error(`init: ${key} is required`);
      }
    }
    return {
      formId: requireNonEmptyString(raw.formId, 'formId'),
      productType: requireNonEmptyString(raw.productType, 'productType'),
      sizePreview: requireBoolean(raw.sizePreview, 'sizePreview'),
      history: requireBoolean(raw.history, 'history'),
      share: requireBoolean(raw.share, 'share'),
      analytics: requireBoolean(raw.analytics, 'analytics'),
      input: raw.input,
      form: raw.form
    };
  };

  const normalizeStringList = (value, label) => {
    if (!Array.isArray(value)) {
      throw new Error(`init: ${label} must be an array`);
    }
    return [
      ...new Set(
        value
          .map((item) => String(item).trim())
          .filter(Boolean)
      )
    ];
  };

  const normalizeInput = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error('init: input must be an object { props, sources, save }');
    }
    return {
      props: normalizeStringList(raw.props, 'input.props'),
      sources: normalizeStringList(raw.sources, 'input.sources'),
      save: normalizeStringList(raw.save, 'input.save')
    };
  };

  /** Boot { [role]: { required } } → full asset rows. */
  const normalizeForm = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error('init: form must be an object { [role]: { required } }');
    }
    const roles = Object.keys(raw);
    if (!roles.length) {
      throw new Error('init: form must include at least one role');
    }
    const out = {};
    for (const role of roles) {
      const def = ASSET_DEFS[role];
      if (!def) {
        throw new Error(
          `init form: unknown role "${role}" — use ${Object.keys(ASSET_DEFS).join(', ')}`
        );
      }
      out[role] = {
        role,
        formKey: def.key,
        kind: def.kind,
        toHistory: def.toHistory === true,
        asBlob: def.kind === 'result',
        from: Array.isArray(def.from) ? def.from.slice() : def.from,
        required: raw[role]?.required === true
      };
    }
    return out;
  };

  /**
   * Expand boot → runtime fields. Partial product boot → throw.
   */
  const buildConfig = (raw = {}) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error('init: config must be an object');
    }

    const product = resolveProductBoot(raw);
    const input = normalizeInput(product.input);
    const form = normalizeForm(product.form);
    const assets = Object.values(form);
    const assetsByKind = {
      result: assets.filter((asset) => asset.kind === 'result'),
      input: assets.filter((asset) => asset.kind === 'input')
    };
    // History restore model: ≤1 source + ≤1 toHistory result + ≤1 toHistory input.
    // Sidecar roles (toHistory:false, e.g. removebg) still write to form but do not block history.
    const historyResultCount = assetsByKind.result.filter((a) => a.toHistory).length;
    const historyInputCount = assetsByKind.input.filter((a) => a.toHistory).length;
    const historyOk = (
      input.sources.length <= 1
      && historyResultCount <= 1
      && historyInputCount <= 1
    );
    return {
      formId: product.formId,
      productType: product.productType,
      sizePreview: product.sizePreview,
      history: product.history,
      share: product.share,
      analytics: product.analytics,
      input,
      form,
      assets,
      assetsByKind,
      formClientAssets: assets.map((asset) => ({
        formKey: asset.formKey,
        required: asset.required,
        asBlob: asset.asBlob
      })),
      needsS3: assetsByKind.input.length > 0,
      historyOk,
      engine: raw.engine ?? null
    };
  };

  /** Single runtime config — set only by createConfig / Pipeline.init. */
  let config = null;

  const requireConfig = () => {
    if (!config) {
      throw new Error('CustomProductOrchestrator: not initialized — call boot() first');
    }
    return config;
  };

  const getFormId = () => requireConfig().formId;
  const getEngine = () => requireConfig().engine;

  const getUploadTarget = () => {
    const target = requireConfig().engine.target;
    return target ? String(target) : null;
  };

  const inputProps = () => requireConfig().input.props;
  const formAssetsByKind = (kind) => requireConfig().assetsByKind[kind];

  // ═══════════════════════════════════════════════════════════
  // 2) MEDIA — urls, start opts, paths, validate, mockups
  // ═══════════════════════════════════════════════════════════

  const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);
  const isDataUrl = (value) => typeof value === 'string' && value.startsWith('data:');
  const mimeFromDataUrl = (value) => {
    if (!isDataUrl(value)) return null;
    const match = /^data:([^;,]+)/i.exec(value);
    return match ? match[1] : null;
  };


  const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });

  const isBlobLike = (value) => (
    typeof Blob !== 'undefined' && value instanceof Blob
  );

  /** File | dataUrl | http | { dataUrl } → string */
  const valueToMediaString = async (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') return value;
    if (isBlobLike(value)) return readBlobAsDataUrl(value);
    if (typeof value === 'object' && value.dataUrl) return String(value.dataUrl);
    return null;
  };

  const normalizeSourcesMap = async (sources) => {
    const out = {};
    for (const key of Object.keys(sources)) {
      const str = await valueToMediaString(sources[key]);
      if (str) out[key] = str;
    }
    return out;
  };

  const isFileLike = (value) => (
    typeof File !== 'undefined' && value instanceof File
  );

  /**
   * Canonical start only: normalize engine.sources + save values to media strings.
   * Returns { engine, save, file } — File from opts.file only (id / history hash).
   */
  const normalizeStartOpts = async (opts = {}) => {
    const { engine: engineIn, save: saveIn, file: fileIn } = opts;

    const requireObject = (value, label) => {
      if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`${label} is required (object)`);
      }
      return { ...value };
    };

    const engine = requireObject(engineIn, 'engine');
    const save = requireObject(saveIn, 'save');

    const { sources: sourcesIn } = engine;
    let rawSources = {};
    if (sourcesIn != null) {
      if (typeof sourcesIn !== 'object' || Array.isArray(sourcesIn)) {
        throw new Error('engine.sources must be an object');
      }
      rawSources = { ...sourcesIn };
    }

    const file = isFileLike(fileIn) ? fileIn : null;

    engine.sources = await normalizeSourcesMap(rawSources);

    for (const key of Object.keys(save)) {
      save[key] = await valueToMediaString(save[key]);
    }

    return { engine, save, file };
  };

  const getByPath = (obj, path) => {
    if (obj == null || path == null || path === '') return null;
    return String(path).split('.').reduce((acc, key) => {
      if (acc == null) return null;
      return acc[key];
    }, obj);
  };

  const resolveFrom = (payload, from) => {
    const paths = Array.isArray(from) ? from : [from];
    for (const path of paths) {
      if (path == null || path === '') continue;
      const value = getByPath(payload, path);
      if (value != null && value !== '') return value;
    }
    return null;
  };

  /**
   * One gate: init coherence (save↔form) + start payload policy.
   * engine/save/sources types: owned by normalizeStartOpts (media path).
   * media:false → props allow-list only (startFromProps).
   */
  const validateInput = (input, formMap, payload = null, { media = true } = {}) => {
    const { props: allowProps, sources: allowSources, save: allowSave } = input;
    const roles = new Set(Object.keys(formMap));

    // save keys are form roles (same names); sources are engine slots — unrelated to result roles
    for (const key of allowSave) {
      if (!roles.has(key)) {
        throw new Error(`input.save '${key}' has no form.${key}`);
      }
    }

    if (!payload) return;

    const { props: propsIn, engine: engineIn, save: saveIn } = payload;

    let props = {};
    if (propsIn != null) {
      if (typeof propsIn !== 'object' || Array.isArray(propsIn)) {
        throw new Error('props must be an object');
      }
      props = propsIn;
    }
    for (const key of Object.keys(props)) {
      if (!allowProps.includes(key)) {
        throw new Error(`props.${key} is not allowed by input.props`);
      }
    }
    for (const key of allowProps) {
      if (props[key] == null || props[key] === '') {
        throw new Error(`props.${key} is required`);
      }
    }

    if (!media) return;

    // Post-normalizeStartOpts (or history rewrite): engine/save are objects
    const { sources = {}, pillowData } = engineIn;
    const save = saveIn;

    const providedSlots = Object.keys(sources).filter(
      (slot) => sources[slot] != null && sources[slot] !== ''
    );
    for (const slot of providedSlots) {
      if (!allowSources.includes(slot)) {
        throw new Error(`engine.sources.${slot} is not allowed by input.sources`);
      }
    }
    const hasPillowData = pillowData != null && pillowData !== '';
    if (!hasPillowData && !providedSlots.length) {
      throw new Error('engine.sources or engine.pillowData is required');
    }
    for (const saveKey of Object.keys(save)) {
      if (!allowSave.includes(saveKey)) {
        throw new Error(`save.${saveKey} is not allowed by input.save`);
      }
      if (save[saveKey] == null || save[saveKey] === '') {
        throw new Error(`save.${saveKey} is empty`);
      }
    }
    const inputAssets = Object.values(formMap).filter((asset) => asset.kind === 'input');
    for (const { required, from, role } of inputAssets) {
      if (!required) continue;
      if (resolveFrom({ save }, from) == null) {
        throw new Error(`save.${role} is required`);
      }
    }
  };

  /**
   * Engine mockup:success → { mockups: { main|back: { noBg, withBg } } }
   * Also derives flat preview / previewWithBg from main (task / history / panel).
   */
  const normalizeMockups = (payload = {}) => {
    const raw = (payload.mockups && typeof payload.mockups === 'object')
      ? payload.mockups
      : {};
    const mockups = {};
    for (const key of Object.keys(raw)) {
      const slot = raw[key];
      if (!slot || typeof slot !== 'object') continue;
      const noBg = slot.noBg || null;
      const withBg = slot.withBg || null;
      if (!noBg && !withBg) continue;
      mockups[key] = { noBg, withBg };
    }
    const main = mockups.main || null;
    return {
      mockups,
      preview: main?.noBg || null,
      previewWithBg: main?.withBg || null
    };
  };


  // ═══════════════════════════════════════════════════════════
  // 3) FORM WRITE + createConfig — cart writes; init constructor
  // ═══════════════════════════════════════════════════════════

  /** Write form assets of `kind` from payload (from → formKey). */
  const writeFormAssetValue = (asset, value) => {
    if (!asset || value == null || value === '') return false;
    const { formKey } = asset;
    adapters.form.setCustomData(formKey, value);
    return true;
  };

  const applyFormAssetsFromPayload = (payload = {}, kind) => {
    for (const { from, ...asset } of formAssetsByKind(kind)) {
      writeFormAssetValue(asset, resolveFrom(payload, from));
    }
  };

  const assertMockupEngine = (engine) => {
    if (!engine || typeof engine !== 'object') {
      throw new Error('init: engine is required');
    }
    if (typeof engine.process !== 'function') {
      throw new Error('init: engine.process is required');
    }
    if (typeof engine.getEl !== 'function') {
      throw new Error('init: engine.getEl is required');
    }
    if (typeof engine.abort !== 'function') {
      throw new Error('init: engine.abort is required');
    }
    if (!engine.target) {
      throw new Error('init: engine.target is required');
    }
    return engine;
  };

  /**
   * Init constructor: build + validate + dep-check → one full runtime config.
   * Pipeline.init only merges boot fields, then assigns `config = createConfig(...)`.
   */
  const createConfig = (raw = {}) => {
    const cfg = buildConfig({
      ...raw,
      engine: assertMockupEngine(raw.engine)
    });

    validateInput(cfg.input, cfg.form);

    const finalAsset = cfg.assets.find((a) => a.role === 'final');
    if (!finalAsset) {
      throw new Error('init: form must include final role (print → _Image_Upload)');
    }
    if (!finalAsset.required) {
      throw new Error('init: form.final.required must be true');
    }

    if (cfg.history && !cfg.historyOk) {
      console.warn(
        '[custom-product-orchestrator-v2] history disabled: needs ≤1 sources + ≤1 toHistory result + ≤1 toHistory input (got sources=%s toHistoryResult=%s toHistoryInput=%s)',
        cfg.input.sources.length,
        cfg.assetsByKind.result.filter((a) => a.toHistory).length,
        cfg.assetsByKind.input.filter((a) => a.toHistory).length
      );
      cfg.history = false;
    }

    if (!window.ImageTaskModule?.create || !window.ImageTaskModule?.update) {
      throw new Error('init: ImageTaskModule.create/update required');
    }
    if (!window.ProductFormCustom) {
      throw new Error('init: ProductFormCustom required');
    }
    if (!window.CustomProductPanelUI) {
      throw new Error('init: CustomProductPanelUI required');
    }
    if (cfg.needsS3 && (!window.SaveToS3?.saveImg || !window.SaveToS3?.abortById)) {
      throw new Error('init: SaveToS3.saveImg/abortById required (form has input roles)');
    }
    if (
      cfg.history
      && (!window.FileHistoryManager?.useFromHistory || !window.FileHistoryManager?.createHistoryItem)
    ) {
      throw new Error('init: FileHistoryManager.useFromHistory/createHistoryItem required');
    }

    return cfg;
  };

  // ═══════════════════════════════════════════════════════════
  // 4) CONSTANTS
  // ═══════════════════════════════════════════════════════════

  if (!window.ImageTaskModule?.STATUS) {
    throw new Error('ImageTaskModule.STATUS required (load image-task before orchestrator)');
  }
  const STATUS = window.ImageTaskModule.STATUS;
  const START_SOURCE = {
    UPLOAD: 'upload',
    HISTORY: 'history',
    SHARE: 'share',
    PROPS: 'props'
  };

  /** Coarse pipeline phase (tracks stay on imageTask.status). */
  const PHASE = {
    IDLE: 'idle',
    RUNNING: 'running',
    SUCCESS: 'success',
    ERROR: 'error'
  };

  /** ImageTask status keys */
  const EVENT_NAMES = {
    MOCKUP: 'mockup',
    SAVE_TO_S3: 'saveToS3'
  };

  const EVENTS = {
    READY: 'customProduct:ready',
    INIT: 'customProduct:init',
    PROPS_INIT: 'customProduct:propsInit',
    STARTED: 'customProduct:started',
    MOCKUP_START: 'customProduct:mockupStart',
    MOCKUP_SUCCESS: 'customProduct:mockupSuccess',
    SAVE_START: 'customProduct:saveStart',
    SAVE_SUCCESS: 'customProduct:saveSuccess',
    SUCCESS: 'customProduct:success',
    ERROR: 'customProduct:error'
  };

  const fileMeta = (file) => (file ? {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified
  } : null);

  /** Slim task for customProduct:* — no pillowData / dataUrl (read-only snapshot). */
  /** Plain analytics-safe error text (no stacks / blobs). */
  const sanitizeErrorMessage = (value, maxLen = 500) => {
    if (value == null || value === '') return null;
    const text = String(value).trim();
    if (!text) return null;
    return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  };

  const toEventTask = (task) => {
    if (!task) return null;
    return {
      id: task.id || null,
      fileOriginalId: task.fileOriginalId || null,
      props: task.props ? { ...task.props } : {},
      s3Url: task.s3Url || null,
      preview: task.preview || null,
      status: {
        mockup: task.status?.mockup ?? null,
        saveToS3: task.status?.saveToS3 ?? null
      },
      error: task.error ? { ...task.error } : null
    };
  };

  // ═══════════════════════════════════════════════════════════
  // 5) ADAPTERS — ports catalog (window.*); panel → CustomProductPanelUI
  // ═══════════════════════════════════════════════════════════

  const adapters = {
    imageTask: {
      create: (originalFileId, props, historyId) => {
        if (!window.ImageTaskModule?.create) {
          throw new Error('ImageTaskModule.create is required');
        }
        return window.ImageTaskModule.create(originalFileId, props, historyId);
      },
      update: (task, updates, taskId = null) => {
        if (!window.ImageTaskModule?.update) {
          throw new Error('ImageTaskModule.update is required');
        }
        return window.ImageTaskModule.update(task, updates, taskId);
      },
      updateForPropsChange: (task, props) => {
        if (!window.ImageTaskModule?.updateForPropsChange) {
          throw new Error('ImageTaskModule.updateForPropsChange is required');
        }
        return window.ImageTaskModule.updateForPropsChange(task, props);
      },
      generateFileOriginalId: (file, contentHash = null) => {
        if (!window.ImageTaskModule?.generateFileOriginalId) {
          throw new Error('ImageTaskModule.generateFileOriginalId is required');
        }
        return window.ImageTaskModule.generateFileOriginalId(file, contentHash);
      },
      hashBlob: async (blob) => {
        if (!window.ImageTaskModule?.hashBlob) {
          throw new Error('ImageTaskModule.hashBlob is required');
        }
        return window.ImageTaskModule.hashBlob(blob);
      }
    },

    form: {
      setCustomData: (key, value) => {
        if (!window.ProductFormCustom?.setCustomData) {
          throw new Error('ProductFormCustom.setCustomData is required');
        }
        return window.ProductFormCustom.setCustomData(getFormId(), key, value);
      },
      clearImageData: () => {
        if (!window.ProductFormCustom?.clearImageData) {
          throw new Error('ProductFormCustom.clearImageData is required');
        }
        return window.ProductFormCustom.clearImageData(getFormId());
      },
      clearCroppingNotes: () => {
        if (!window.ProductFormCustom?.clearCroppingNotes) {
          throw new Error('ProductFormCustom.clearCroppingNotes is required');
        }
        return window.ProductFormCustom.clearCroppingNotes(getFormId());
      },
      getProp: (key) => {
        if (!window.ProductFormCustom?.getProp) {
          throw new Error('ProductFormCustom.getProp is required');
        }
        return window.ProductFormCustom.getProp(getFormId(), key);
      },
      getMockupProps: () => {
        const props = {};
        for (const key of inputProps()) {
          props[key] = adapters.form.getProp(key);
        }
        return props;
      },
      updateProp: (prop, value, options = {}) => {
        if (!window.ProductFormCustom?.updateProp) {
          throw new Error('ProductFormCustom.updateProp is required');
        }
        return window.ProductFormCustom.updateProp(getFormId(), prop, value, options);
      },
      syncProps: (props = {}, options = {}) => {
        if (!props || typeof props !== 'object') return;
        for (const key of Object.keys(props)) {
          if (props[key] == null || props[key] === '') continue;
          adapters.form.updateProp(key, props[key], options);
        }
      },
      enableButtons: () => {
        if (!window.ProductFormCustom?.enableButton) {
          throw new Error('ProductFormCustom.enableButton is required');
        }
        return window.ProductFormCustom.enableButton(getFormId());
      },
      disableButtons: () => {
        if (!window.ProductFormCustom?.disableButton) {
          throw new Error('ProductFormCustom.disableButton is required');
        }
        return window.ProductFormCustom.disableButton(getFormId());
      },
      hasRequiredAssets: () => {
        if (typeof window.ProductFormCustom?.hasRequiredAssets !== 'function') {
          throw new Error('ProductFormCustom.hasRequiredAssets is required');
        }
        return window.ProductFormCustom.hasRequiredAssets(getFormId());
      }
    },

    engine: {
      process: (opts) => {
        const engine = getEngine();
        if (typeof engine.process !== 'function') {
          throw new Error('engine.process is required');
        }
        return engine.process(opts);
      },
      abort: () => {
        const engine = getEngine();
        if (typeof engine.abort !== 'function') {
          throw new Error('engine.abort is required');
        }
        return engine.abort();
      }
    },

    s3: {
      /** Caller passes { requestId, role }; result via saveToS3:success|error only. */
      saveImg: (dataUrl, fileType, eventData = {}) => {
        if (!window.SaveToS3?.saveImg) {
          throw new Error('SaveToS3.saveImg is required');
        }
        window.SaveToS3.saveImg(dataUrl, fileType, eventData);
      },
      abort: () => {
        if (!requireConfig().needsS3) return;
        if (!window.SaveToS3?.abort) {
          throw new Error('SaveToS3.abort is required');
        }
        return window.SaveToS3.abort();
      },
      abortById: (requestId) => {
        if (!requireConfig().needsS3) return false;
        if (!window.SaveToS3?.abortById) {
          throw new Error('SaveToS3.abortById is required');
        }
        return window.SaveToS3.abortById(requestId);
      }
    },

    history: {
      useFromHistory: (fileOriginalId, s3Url, props) => {
        if (!window.FileHistoryManager?.useFromHistory) {
          throw new Error('FileHistoryManager.useFromHistory is required');
        }
        return window.FileHistoryManager.useFromHistory(fileOriginalId, s3Url, props);
      },
      createHistoryItem: (data) => {
        if (!window.FileHistoryManager?.createHistoryItem) {
          throw new Error('FileHistoryManager.createHistoryItem is required');
        }
        return window.FileHistoryManager.createHistoryItem(data);
      }
    },

    upload: {
      clear: () => {
        const api = window.ImageUploadInput;
        if (!api?.clearForTarget) {
          throw new Error('ImageUploadInput.clearForTarget is required');
        }
        return api.clearForTarget(getUploadTarget());
      }
    },

    panel: {
      updatePreview: (preview) => {
        if (typeof window.CustomProductPanelUI?.updatePreview !== 'function') {
          throw new Error('CustomProductPanelUI.updatePreview is required');
        }
        return window.CustomProductPanelUI.updatePreview(preview);
      },
      clearPreview: () => {
        if (typeof window.CustomProductPanelUI?.clearPreview !== 'function') {
          throw new Error('CustomProductPanelUI.clearPreview is required');
        }
        return window.CustomProductPanelUI.clearPreview();
      },
      reset: () => {
        if (typeof window.CustomProductPanelUI?.reset !== 'function') {
          throw new Error('CustomProductPanelUI.reset is required');
        }
        return window.CustomProductPanelUI.reset();
      },
      applySizePreview: (sizeKey, preview) => {
        if (typeof window.CustomProductPanelUI?.applySizePreview !== 'function') {
          throw new Error('CustomProductPanelUI.applySizePreview is required');
        }
        return window.CustomProductPanelUI.applySizePreview(sizeKey, preview);
      },
      showError: (type) => {
        if (typeof window.CustomProductPanelUI?.showError !== 'function') {
          throw new Error('CustomProductPanelUI.showError is required');
        }
        return window.CustomProductPanelUI.showError(type);
      },
      hideError: () => {
        if (typeof window.CustomProductPanelUI?.hideError !== 'function') {
          throw new Error('CustomProductPanelUI.hideError is required');
        }
        return window.CustomProductPanelUI.hideError();
      },
      showProductBlock: () => {
        if (typeof window.CustomProductPanelUI?.showProductBlock !== 'function') {
          throw new Error('CustomProductPanelUI.showProductBlock is required');
        }
        return window.CustomProductPanelUI.showProductBlock();
      },
      hideProductBlock: () => {
        if (typeof window.CustomProductPanelUI?.hideProductBlock !== 'function') {
          throw new Error('CustomProductPanelUI.hideProductBlock is required');
        }
        return window.CustomProductPanelUI.hideProductBlock();
      },
      mapErrorType: (errorType) => {
        if (typeof window.CustomProductPanelUI?.mapErrorType !== 'function') {
          throw new Error('CustomProductPanelUI.mapErrorType is required');
        }
        return window.CustomProductPanelUI.mapErrorType(errorType);
      }
    }
  };


  // ═══════════════════════════════════════════════════════════
  // 6) PIPELINE — commands → start(); S3; state; phase
  // ═══════════════════════════════════════════════════════════

  const snapshotConfig = () => {
    if (!config) return null;
    const { input, form, assets, assetsByKind, formClientAssets, engine, ...rest } = config;
    const copyAsset = (asset) => ({
      ...asset,
      from: Array.isArray(asset.from) ? asset.from.slice() : asset.from
    });
    return {
      ...rest,
      input: {
        props: input.props.slice(),
        sources: input.sources.slice(),
        save: input.save.slice()
      },
      form: Object.fromEntries(
        Object.entries(form).map(([role, row]) => [role, copyAsset(row)])
      ),
      assets: assets.map(copyAsset),
      assetsByKind: {
        result: assetsByKind.result.map(copyAsset),
        input: assetsByKind.input.map(copyAsset)
      },
      formClientAssets: formClientAssets.map((asset) => ({ ...asset })),
      engine
    };
  };

  const Pipeline = (() => {
    const state = {
      phase: PHASE.IDLE,
      imageTask: null,
      generation: 0,
      s3JobGeneration: 0,
      completedGeneration: -1,
      pipelineSource: null, // upload | history | share | props
      pipelineBag: {},
      s3Pending: new Set(),
      s3RolesDone: new Set(),
      s3HadUpload: false,
      s3SaveStartEmitted: false,
      s3UrlsByRole: {},
      /** role → SaveToS3 requestId (events must echo role + requestId) */
      s3RequestByRole: new Map()
    };

    const setPhase = (phase) => {
      state.phase = phase;
    };

    const beginPipelineGeneration = (source) => {
      state.generation += 1;
      state.pipelineSource = source;
      state.pipelineBag = {};
      state.s3Pending = new Set();
      state.s3RolesDone = new Set();
      state.s3HadUpload = false;
      state.s3SaveStartEmitted = false;
      state.s3UrlsByRole = {};
      state.s3RequestByRole = new Map();
      state.s3JobGeneration = state.generation;
      setPhase(PHASE.RUNNING);
    };

    // Task ↔ form ↔ UI
    const StateCoordinator = (() => {
      const updateTask = (updates) => {
        if (!state.imageTask) return false;
        const updated = adapters.imageTask.update(state.imageTask, updates, state.imageTask.id);
        if (updated) {
          state.imageTask = updated;
          return true;
        }
        return false;
      };

      const updateImageTaskForPropsChange = (props) => {
        if (!props || !state.imageTask) {
          error('unknown');
          return;
        }
        const updatedTask = adapters.imageTask.updateForPropsChange(state.imageTask, props);
        if (updatedTask) {
          state.imageTask = updatedTask;
        }
      };

      const setS3Url = (url) => {
        if (!url) return;
        updateTask({ s3Url: url });
      };

      const setOriginalS3Url = (url) => {
        if (!url) return;
        updateTask({ originalS3Url: url });
      };

      const savePillowData = (data) => {
        updateTask({ pillowData: data });
      };

      /** Pass start.engine through to process (sources and/or pillowData). */
      const runEngine = (payload) => {
        Promise.resolve()
          .then(() => adapters.engine.process(payload))
          .catch((error) => {
            StatusManager.markEventError(
              EVENT_NAMES.MOCKUP,
              adapters.panel.mapErrorType(error?.message),
              {
                generation: payload.generation,
                message: error?.message || null
              }
            );
          });
      };

      /** Pass start.engine through to process. Gated by validateInput; sources are media strings. */
      const runEngineProcess = (engineIn, props = {}) => {
        const sources = engineIn.sources;
        const pillowData = engineIn.pillowData ?? null;
        const hasSources = sources && typeof sources === 'object'
          && Object.keys(sources).some((slot) => sources[slot] != null && sources[slot] !== '');

        if (hasSources) {
          if (typeof sources.main === 'string') updateTask({ dataUrl: sources.main });
          runEngine({
            sources,
            props,
            generation: state.generation
          });
          return;
        }

        savePillowData(pillowData);
        runEngine({
          pillowData,
          props,
          generation: state.generation
        });
      };

      const setProps = (props = {}, formUpdate = false, options = {}) => {
        if (!props || typeof props !== 'object') return;
        if (state.imageTask) {
          const next = { ...(state.imageTask.props || {}), ...props };
          const prev = state.imageTask.props || {};
          const identityChanged = config.input.props.some(
            (key) => String(next[key] ?? '') !== String(prev[key] ?? '')
          );
          if (identityChanged) {
            updateImageTaskForPropsChange(next);
          }
        }
        if (formUpdate) {
          adapters.form.syncProps(props, options);
        }
        runEngine({
          props,
          generation: state.generation
        });
      };

      /** Apply engine mockup:success payload → task preview*, all form kind:result, panel. */
      const setPreview = (payload = null) => {
        const detail = payload && typeof payload === 'object' ? { ...payload } : {};
        const normalized = normalizeMockups(detail);
        detail.mockups = normalized.mockups;

        updateTask({
          preview: normalized.preview,
          previewWithBg: normalized.previewWithBg
        });

        state.pipelineBag = {
          ...state.pipelineBag,
          engine: {
            ...(state.pipelineBag.engine || {}),
            ...detail
          }
        };

        applyFormAssetsFromPayload(detail, 'result');

        if (!normalized.preview) adapters.panel.clearPreview();
        else adapters.panel.updatePreview(normalized.preview);
      };

      const setTrackStatus = (track, status) => {
        if (!state.imageTask) return;
        updateTask({
          status: {
            ...state.imageTask.status,
            [track]: status
          }
        });
      };
      const setMockupTrackState = (status) => setTrackStatus('mockup', status);
      const setSaveToS3State = (status) => setTrackStatus('saveToS3', status);

      const clearState = () => {
        state.imageTask = null;
        adapters.form.clearImageData();
        adapters.panel.reset();
        adapters.form.clearCroppingNotes();
      };

      const initialState = () => {
        clearState();
        setPhase(PHASE.IDLE);
        adapters.panel.hideError();
        adapters.panel.hideProductBlock();
        adapters.upload.clear();
      };

      const defaultState = () => {
        clearState();
        adapters.panel.hideError();
      };

      const error = (type) => {
        adapters.panel.showError(type);
        clearState();
      };

      return {
        setS3Url,
        setOriginalS3Url,
        runEngineProcess,
        setProps,
        setPreview,
        setMockupTrackState,
        setSaveToS3State,
        savePillowData,
        clearState,
        defaultState,
        initialState,
        error
      };
    })();

    const StatusManager = (() => {
      const processInit = (file, s3Url, source = START_SOURCE.UPLOAD) => {
        adapters.form.disableButtons();
        adapters.engine.abort();
        adapters.s3.abort();
        beginPipelineGeneration(source);
        StateCoordinator.defaultState();

        EventService.dispatch(EVENTS.INIT, {
          s3Url: s3Url || null,
          file: fileMeta(file)
        });
      };

      const propsStart = (props = {}) => {
        adapters.form.disableButtons();
        // Skip engine.abort — clearing src would wipe viewer pillowdata mid props update
        adapters.s3.abort();
        beginPipelineGeneration(START_SOURCE.PROPS);
        StateCoordinator.setPreview(null);
        StateCoordinator.setMockupTrackState(STATUS.PENDING);
        saveToS3Completed(null, STATUS.SKIP);

        EventService.dispatch(EVENTS.PROPS_INIT, { props });
      };

      const processStarted = (task, file) => {
        EventService.dispatch(EVENTS.STARTED, {
          file: fileMeta(file)
        });
      };

      const processEnd = () => {
        if (!adapters.form.hasRequiredAssets()) {
          console.warn('[custom-product-orchestrator-v2] required form fields missing — ATC stays disabled');
          return;
        }
        // SUCCESS only if still running (error path sets PHASE.ERROR first)
        if (state.phase === PHASE.RUNNING) setPhase(PHASE.SUCCESS);
        adapters.form.enableButtons();
      };

      const processError = (errorType) => {
        setPhase(PHASE.ERROR);
        StateCoordinator.error(errorType);
        adapters.form.enableButtons();
      };

      const abortPending = () => {
        adapters.engine.abort();
        adapters.s3.abort();
      };

      const mockupCompleted = (data = {}, status = STATUS.SUCCESS) => {
        const pillowData = data.pillowData ?? null;
        StateCoordinator.setMockupTrackState(status);
        StateCoordinator.savePillowData(pillowData);
        // Pass full mockup:success detail (mockups + removebg + …), not mockups only
        StateCoordinator.setPreview(data);
      };

      const mockupError = () => {
        StateCoordinator.setMockupTrackState(STATUS.ERROR);
      };

      const saveToS3Completed = (url, status = STATUS.SUCCESS) => {
        StateCoordinator.setSaveToS3State(status);
        if (url) {
          StateCoordinator.setS3Url(url);
        }
      };

      const saveToS3Error = () => {
        StateCoordinator.setSaveToS3State(STATUS.ERROR);
      };

      const checkAllCompleted = () => {
        if (!state.imageTask) return false;
        const { status } = state.imageTask;
        const mockupStatus = state.imageTask?.status?.mockup ?? null;
        const mockupDone = mockupStatus === STATUS.SUCCESS || mockupStatus === STATUS.SKIP;
        const { saveToS3 } = status;
        const saveToS3Done = saveToS3 === STATUS.SUCCESS || saveToS3 === STATUS.SKIP;
        const allSuccess = mockupDone && saveToS3Done;
        const anyError = mockupStatus === STATUS.ERROR || saveToS3 === STATUS.ERROR;

        if (allSuccess) {
          if (state.completedGeneration === state.generation) return true;
          state.completedGeneration = state.generation;
          processEnd();
          EventService.dispatch(EVENTS.SUCCESS, {});
          return true;
        }

        if (anyError) {
          abortPending();
          setPhase(PHASE.ERROR);
          processEnd();
          return true;
        }

        return false;
      };

      const markEventSuccess = (eventName, data = {}, status = STATUS.SUCCESS) => {
        if (eventName === EVENT_NAMES.MOCKUP) {
          const gen = data.generation;
          if (gen != null && gen !== state.generation) return;
          mockupCompleted(data, status);
        }
        if (eventName === EVENT_NAMES.SAVE_TO_S3) {
          if (state.s3JobGeneration !== state.generation) return;
          saveToS3Completed(data.url || null, status);
        }
        checkAllCompleted();
      }; 

      const markEventError = (eventName, errorType, data = {}) => {
        if (eventName === EVENT_NAMES.MOCKUP) {
          const gen = data.generation;
          if (gen != null && gen !== state.generation) return;
          mockupError();
        } else if (eventName === EVENT_NAMES.SAVE_TO_S3) {
          if (state.s3JobGeneration !== state.generation) return;
          saveToS3Error();
        } else {
          return;
        }

        // Populate before ERROR dispatch — analytics reads imageTaskSlim.error
        const type = errorType || 'unknown';
        if (state.imageTask) {
          const updated = adapters.imageTask.update(state.imageTask, {
            error: {
              event: eventName,
              type,
              message: sanitizeErrorMessage(data.message) || type
            }
          }, state.imageTask.id);
          if (updated) state.imageTask = updated;
        }

        checkAllCompleted();
        EventService.dispatch(EVENTS.ERROR, {
          errorType: type,
          message: sanitizeErrorMessage(data.message) || type
        });

        processError(type);
      };

      return {
        processInit,
        propsStart,
        processStarted,
        processEnd,
        processError,
        abortPending,
        mockupCompleted,
        mockupError,
        saveToS3Completed,
        saveToS3Error,
        checkAllCompleted,
        markEventSuccess,
        markEventError
      };
    })();

    const createAndSetTask = (sourceFile, props, historyFileId, fileOriginalId) => {
      const resolvedOriginalId = fileOriginalId
        || (sourceFile ? adapters.imageTask.generateFileOriginalId(sourceFile) : null);
      const imageTask = adapters.imageTask.create(resolvedOriginalId, props, historyFileId);
      if (!imageTask) {
        throw new Error('failed to create image task - ImageTaskModule may not be loaded');
      }

      state.imageTask = imageTask;
      return imageTask;
    };

    const handleProcessingError = (message, functionName) => {
      console.error(`[Pipeline:${functionName}] ${message}`);
      adapters.form.enableButtons();
      StateCoordinator.error(adapters.panel.mapErrorType(message));
    };

    // Source-role form writes: http → form; data: → S3 → form (one SAVE_TO_S3 track).
    const S3AssetsSync = (() => {
      const mergeBag = (patch = {}) => {
        state.pipelineBag = { ...state.pipelineBag, ...patch };
        return state.pipelineBag;
      };

      const writeRoleToForm = (asset, url) => {
        if (!writeFormAssetValue(asset, url)) return;
        state.s3UrlsByRole[asset.role] = url;
        // Mirror input roles onto imageTask for history (boot form/input).
        if (asset.role === 'final') {
          StateCoordinator.setS3Url(url);
          state.pipelineBag.s3Url = url;
        } else if (asset.role === 'original') {
          StateCoordinator.setOriginalS3Url(url);
        }
      };

      const emitSaveStartOnce = () => {
        if (state.s3SaveStartEmitted) return;
        state.s3SaveStartEmitted = true;
        EventService.dispatch(EVENTS.SAVE_START, {});
      };

      const tryComplete = () => {
        if (state.s3JobGeneration !== state.generation) return;
        if (state.s3Pending.size > 0) return;

        const assets = config.assetsByKind.input;
        if (!assets.length) {
          StatusManager.markEventSuccess(EVENT_NAMES.SAVE_TO_S3, { url: null }, STATUS.SKIP);
          return;
        }

        const allDone = assets.every((asset) => state.s3RolesDone.has(asset.role));
        if (!allDone) return;

        const status = state.s3HadUpload ? STATUS.SUCCESS : STATUS.SKIP;
        // Update task before event so imageTask.status.saveToS3 is current
        StatusManager.saveToS3Completed(state.pipelineBag.s3Url || null, status);
        EventService.dispatch(EVENTS.SAVE_SUCCESS, {
          skipped: !state.s3HadUpload
        });
        StatusManager.checkAllCompleted();
      };

      // form.from → formKey; upload only when value is data: (input roles only — not result mockups)
      const sync = (patch = {}, opts = {}) => {
        mergeBag(patch);
        state.s3JobGeneration = state.generation;

        const assets = config.assetsByKind.input;
        if (!assets.length) {
          tryComplete();
          return;
        }

        const fileType = state.pipelineBag.fileType || 'image/jpeg';

        for (const asset of assets) {
          if (state.s3RolesDone.has(asset.role) || state.s3Pending.has(asset.role)) {
            continue;
          }

          const value = resolveFrom(state.pipelineBag, asset.from);

          if (isHttpUrl(value)) {
            writeRoleToForm(asset, value);
            state.s3RolesDone.add(asset.role);
            continue;
          }

          if (isDataUrl(value)) {
            state.s3Pending.add(asset.role);
            state.s3HadUpload = true;
            emitSaveStartOnce();
            const requestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `s3_${state.generation}_${asset.role}_${Date.now()}`;
            state.s3RequestByRole.set(asset.role, requestId);
            try {
              adapters.s3.saveImg(value, fileType, {
                requestId,
                role: asset.role
              });
            } catch (error) {
              onRoleError({
                requestId,
                role: asset.role,
                errorType: 'network',
                message: error?.message || null
              });
              return;
            }
            continue;
          }

          if (opts.finalize) {
            state.s3RolesDone.add(asset.role);
          }
        }

        tryComplete();
      };

      // SaveToS3 is a shared bus (orch + towel cutout/edit/…). Only accept
      // callbacks that match a pending role+requestId for this generation;
      // unmatched events are foreign — ignore, do not fail the pipeline.
      const isOwnedS3Callback = (eventData = {}) => {
        const requestId = eventData.requestId || null;
        const role = eventData.role || null;
        if (!role || !requestId) return false;
        const expectedRequestId = state.s3RequestByRole.get(role);
        return !!expectedRequestId && expectedRequestId === requestId;
      };

      const onRoleSuccess = (eventData = {}) => {
        if (state.s3JobGeneration !== state.generation) return;
        if (!isOwnedS3Callback(eventData)) return;

        const role = eventData.role;
        const url = eventData.url || eventData.response?.url || null;
        const asset = config.assetsByKind.input.find((row) => row.role === role);

        if (asset && url) {
          writeRoleToForm(asset, url);
        } else if (asset?.required && !url) {
          state.s3RequestByRole.delete(role);
          StatusManager.markEventError(EVENT_NAMES.SAVE_TO_S3, 'network', {
            role,
            message: `saveToS3 missing url for required role: ${role}`
          });
          return;
        }

        state.s3RequestByRole.delete(role);
        state.s3Pending.delete(role);
        state.s3RolesDone.add(role);
        tryComplete();
      };

      const onRoleError = (errorData = {}) => {
        if (state.s3JobGeneration !== state.generation) return;
        if (!isOwnedS3Callback(errorData)) return;

        const requestId = errorData.requestId || null;
        const role = errorData.role || null;
        if (role) {
          state.s3Pending.delete(role);
          state.s3RequestByRole.delete(role);
        }
        for (const pendingRequestId of state.s3RequestByRole.values()) {
          adapters.s3.abortById(pendingRequestId);
        }
        state.s3RequestByRole.clear();
        state.s3Pending.clear();
        StatusManager.markEventError(
          EVENT_NAMES.SAVE_TO_S3,
          errorData.errorType || 'network',
          {
            role,
            requestId,
            message: errorData.message || errorData.errorType || 'network'
          }
        );
      };

      return {
        mergeBag,
        sync,
        onRoleSuccess,
        onRoleError,
        tryComplete
      };
    })();


    /** Props from opts.props only (keys = input.props). Call after validateInput. */
    const resolveProps = (opts = {}) => {
      const raw = opts.props;
      const props = {};
      for (const key of config.input.props) {
        props[key] = String(raw[key]);
      }
      return props;
    };

    /**
     * init(boot) — full product boot required.
     */
    const applyPipelineConfig = (next = {}) => {
      config = createConfig(next);

      const syncFormConfig = (attempt = 0) => {
        const okAssets = window.ProductFormCustom?.setAssets?.(
          config.formId,
          config.formClientAssets
        );
        if (okAssets === false && attempt < 20) {
          setTimeout(() => syncFormConfig(attempt + 1), 50);
        }
      };
      syncFormConfig();

      return snapshotConfig();
    };

    // start({ engine, save, props }) — history cache-hit before validateInput.
    const start = async (opts = {}) => {
      try {
        requireConfig();
        const {
          source = START_SOURCE.UPLOAD,
          fileId = null,
          fileOriginalId: fileOriginalIdIn = null
        } = opts;

        if (source === START_SOURCE.PROPS) {
          if (!state.imageTask) {
            throw new Error('startFromProps: no live imageTask (upload/history/share first)');
          }
          validateInput(config.input, config.form, opts, { media: false });
          const props = resolveProps(opts);
          StatusManager.propsStart(props);
          StateCoordinator.setProps(props);
          return;
        }

        let prepared = await normalizeStartOpts(opts);
        let { engine, save, file: sourceFile } = prepared;
        let historyFileId = fileId;
        let fileOriginalId = fileOriginalIdIn;
        let sourceFileHash;

        const getSourceFileHash = async () => {
          if (!sourceFile) return null;
          if (sourceFileHash === undefined) {
            sourceFileHash = await adapters.imageTask.hashBlob(sourceFile);
          }
          return sourceFileHash;
        };

        // Upload + history on: same file+props → pillowData (skip re-process)
        // restoreCache: previews for synthetic mockup SKIP (WC may no-op identical pillowdata)
        let restoreCache = null;
        if (
          source === START_SOURCE.UPLOAD
          && config.history
          && engine.pillowData == null
          && sourceFile
        ) {
          const sourceSlots = Object.keys(engine.sources).filter(
            (slot) => engine.sources[slot] != null && engine.sources[slot] !== ''
          );
          const backOnly = sourceSlots.length === 1 && sourceSlots[0] === 'back';
          if (!backOnly) {
            try {
              // Props not validated yet — form snapshot + opts for lookup only
              const propsHint = {
                ...adapters.form.getMockupProps(),
                ...(opts.props && typeof opts.props === 'object' ? opts.props : {})
              };
              const contentHash = await getSourceFileHash();
              const originalId = fileOriginalId
                || adapters.imageTask.generateFileOriginalId(sourceFile, contentHash);
              const s3Hint = save.final || null;

              const hit = adapters.history.useFromHistory(originalId, s3Hint, propsHint);

              // Incomplete history item → cache miss (run normal upload)
              if (hit?.fileId && hit.pillowData && hit.s3Url) {
                engine = { pillowData: hit.pillowData };
                save = { final: hit.s3Url };
                historyFileId = hit.fileId;
                fileOriginalId = hit.fileOriginalId || originalId;
                prepared = { engine, save, file: sourceFile };
                restoreCache = {
                  preview: hit.preview || null,
                  previewWithBg: hit.previewWithBg || hit.preview || null
                };
              } else {
                fileOriginalId = fileOriginalId || originalId;
              }
            } catch (err) {
              console.warn('[custom-product-orchestrator-v2] history lookup failed', err);
            }
          }
        }

        // History / share click — load previews from history item when available
        if (
          !restoreCache
          && (source === START_SOURCE.HISTORY || source === START_SOURCE.SHARE)
          && historyFileId
          && window.FileHistoryManager?.getFileFromHistory
        ) {
          const historyItem = window.FileHistoryManager.getFileFromHistory(historyFileId);
          if (historyItem?.preview || historyItem?.previewWithBg) {
            restoreCache = {
              preview: historyItem.preview || null,
              previewWithBg: historyItem.previewWithBg || historyItem.preview || null
            };
          }
        }

        // One gate after normalize + optional history rewrite
        validateInput(config.input, config.form, { props: opts.props, ...prepared });
        const props = resolveProps(opts);

        if (source === START_SOURCE.HISTORY || source === START_SOURCE.SHARE) {
          adapters.form.syncProps(props, {
            source: source === START_SOURCE.HISTORY ? 'mockupHistory' : 'pillow-preview'
          });
        }

        const pillowData = engine.pillowData ?? null;
        const finalMedia = save.final || null;
        const mainSource = engine.sources?.main || null;
        const hasSources = !!(
          engine.sources
          && typeof engine.sources === 'object'
          && Object.keys(engine.sources).some(
            (slot) => engine.sources[slot] != null && engine.sources[slot] !== ''
          )
        );
        const finalS3Url = isHttpUrl(finalMedia) ? finalMedia : null;
        const previewDataUrl = isDataUrl(mainSource)
          ? mainSource
          : (isDataUrl(finalMedia) ? finalMedia : null);
        const fileType = sourceFile?.type
          || mimeFromDataUrl(finalMedia)
          || mimeFromDataUrl(mainSource)
          || 'image/jpeg';

        adapters.panel.showProductBlock();
        StatusManager.processInit(sourceFile, finalS3Url, source);

        if (!fileOriginalId && sourceFile) {
          const contentHash = await getSourceFileHash();
          fileOriginalId = adapters.imageTask.generateFileOriginalId(sourceFile, contentHash);
        }

        const task = createAndSetTask(sourceFile, props, historyFileId, fileOriginalId);

        S3AssetsSync.sync({
          engine,
          save,
          s3Url: finalS3Url,
          dataUrl: previewDataUrl,
          fileType,
          pillowData
        }, { finalize: true });

        // Best-effort viewer update (WC may no-op if pillowdata unchanged)
        StateCoordinator.runEngineProcess(engine, props);

        // Before synthetic complete — analytics already STARTed on upload selected;
        // STARTED must fire while processActive so it dedupes (not a 2nd START).
        StatusManager.processStarted(task, sourceFile);

        // PillowData restore (history hit / history UI): synthetic SKIP like legacy —
        // do not wait for mockup:success (identical pillowdata never re-renders).
        if (!hasSources && pillowData) {
          const preview = restoreCache?.preview || null;
          const previewWithBg = restoreCache?.previewWithBg || preview;
          const mockupDetail = {
            source: 'restore',
            generation: state.generation,
            pillowData,
            mockups: (preview || previewWithBg)
              ? { main: { noBg: preview, withBg: previewWithBg } }
              : {}
          };
          StatusManager.mockupCompleted(mockupDetail, STATUS.SKIP);
          EventService.dispatch(EVENTS.MOCKUP_SUCCESS, {});
          StatusManager.checkAllCompleted();
        }
      } catch (error) {
        handleProcessingError(error.message, 'start');
      }
    };

    /**
     * Upload entry — canonical start shape:
     *   {
     *     props: { [mockupProp]: value },
     *     engine: { sources: { main?, back? } } | { pillowData },
     *     save: { final, finalBack?, original?, … }
     *   }
     * History cache-hit lives in start (before validateInput). Do not pass source.
     */
    const applySizePreview = (sizeKey) => {
      if (!config.sizePreview || !sizeKey) return;
      const mainPreview = state.imageTask?.preview || null;
      if (!mainPreview) return;
      adapters.panel.applySizePreview(sizeKey, mainPreview);
    };

    const onPropChange = (detail = {}) => {
      const prop = detail.prop || null;
      const value = detail.value ?? null;
      if (!prop || value == null || value === '') return;
      if (detail.source === 'mockupHistory' || detail.source === 'pillow-preview') return;

      if (prop === 'size') {
        applySizePreview(detail.sizeKey || value);
        return;
      }

      if (!config.input.props.includes(prop)) return;
      return startFromProps({
        props: {
          ...adapters.form.getMockupProps(),
          [prop]: value
        }
      });
    };

    // ── Commands → single start() (product strategy = boot config) ──
    const startFromUpload = (opts = {}) => start({ ...opts, source: START_SOURCE.UPLOAD });

    const startFromHistory = (opts = {}) => start({ ...opts, source: START_SOURCE.HISTORY });

    const startFromShare = (opts = {}) => start({ ...opts, source: START_SOURCE.SHARE });

    const startFromProps = (opts = {}) => start({ ...opts, source: START_SOURCE.PROPS });

    // ── Inbound handlers (EventService only) — keep call order identical ──
    const onMockupStart = () => {
      EventService.dispatch(EVENTS.MOCKUP_START, {});
    };

    const onMockupSuccess = (detail = {}) => {
      const { generation } = detail;
      const normalized = normalizeMockups(detail);
      // Inbound engine `source` (generate|restore|props) — viewer colorbg→props, …
      const engineSource = detail.source || null;
      const mockupStatus = engineSource === 'restore' ? STATUS.SKIP : STATUS.SUCCESS;
      const pillow = detail.pillowData ?? null;
      const gen = generation ?? state.generation ?? null;

      // Stale job (engine generation behind pipeline)
      if (generation != null && generation !== state.generation) return;

      // Update task first so customProduct:mockupSuccess carries fresh imageTaskSlim
      StatusManager.mockupCompleted({
        ...detail,
        ...normalized,
        pillowData: pillow,
        generation: gen
      }, mockupStatus);

      EventService.dispatch(EVENTS.MOCKUP_SUCCESS, {});

      StatusManager.checkAllCompleted();
    };

    const onMockupError = (errorData = {}) => {
      const generation = errorData.generation;
      const rawMessage = errorData.message || null;
      const message = (rawMessage || '').toLowerCase();

      if (!message) {
        StatusManager.markEventError(
          EVENT_NAMES.MOCKUP,
          errorData.type || 'unknown',
          { generation, message: rawMessage }
        );
        return;
      }

      const errorType = errorData.type || (
        message.includes('removebg') ? 'removebg'
          : message.includes('background') ? 'removebg'
            : message.includes('timeout') ? 'network'
              : 'mockup'
      );

      StatusManager.markEventError(EVENT_NAMES.MOCKUP, errorType, {
        generation,
        message: rawMessage
      });
    };

    const onS3Success = (eventData) => S3AssetsSync.onRoleSuccess(eventData);
    const onS3Error = (errorData) => S3AssetsSync.onRoleError(errorData);

    return {
      init: (next) => applyPipelineConfig(next || {}),
      getConfig: () => snapshotConfig(),
      startFromUpload,
      startFromHistory,
      startFromShare,
      startFromProps,
      getImageTask: () => state.imageTask || null,
      /** Defaults merged into every customProduct:* detail */
      getEventDefaults: () => ({
        pipelineSource: state.pipelineSource || null,
        generation: state.generation ?? null,
        imageTaskSlim: toEventTask(state.imageTask),
        props: adapters.form.getMockupProps()
      }),
      initialState: () => StateCoordinator.initialState(),
      onMockupStart,
      onMockupSuccess,
      onMockupError,
      onS3Success,
      onS3Error,
      onPropChange
    };
  })();

  // ═══════════════════════════════════════════════════════════
  // 7) EVENTS
  //
  // INBOUND (listen → Pipeline handler):
  //   mockup:start              → onMockupStart
  //   mockup:success            → onMockupSuccess
  //   mockup:error              → onMockupError
  //   saveToS3:success          → onS3Success
  //   saveToS3:error            → onS3Error
  //   productFormCustom:propChange → onPropChange
  //   (upload success → wiring → startFromUpload — not here)
  //
  // OUTBOUND (EventService.dispatch / EVENTS.*) — same detail shape always
  //   (see file header: customProduct:* detail + imageTaskSlim).
  //   History on SUCCESS → getImageTask() (live; pillowData), not detail.imageTaskSlim.
  //   (+ mockupHistory:loadFile → startFromHistory when config.history)
  // ═══════════════════════════════════════════════════════════

  const matchesUploadTarget = (rawTarget) => {
    if (!config) return null;
    const target = String(rawTarget || '').trim();
    const uploadTarget = getUploadTarget();
    if (target && uploadTarget && target !== uploadTarget) return null;
    return target || uploadTarget;
  };

  const EventService = {
    /**
     * One detail shape for all customProduct:* — missing keys are null.
     * pipelineSource / generation / imageTaskSlim / props default from Pipeline state
     * unless the caller overrides (or passes null explicitly via detail).
     */
    dispatch: (eventName, detail = {}) => {
      const defaults = Pipeline.getEventDefaults?.() || {};
      const pick = (key, fallback = null) => (
        Object.prototype.hasOwnProperty.call(detail, key) ? detail[key] : fallback
      );

      const payload = {
        pipelineSource: pick('pipelineSource', defaults.pipelineSource ?? null),
        generation: pick('generation', defaults.generation ?? null),
        imageTaskSlim: pick('imageTaskSlim', defaults.imageTaskSlim ?? null),
        props: pick('props', defaults.props ?? null),
        file: pick('file', null),
        preview: pick('preview', null),
        s3Url: pick('s3Url', null),
        url: pick('url', null),
        urls: pick('urls', null),
        skipped: pick('skipped', null),
        errorType: pick('errorType', null),
        message: pick('message', null),
        target: pick('target', null),
        config: pick('config', null)
      };

      document.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        detail: payload
      }));
    },

    setupInternalHandlers: () => {
      // Native listener: jQuery ":" is a namespace and misses CustomEvent
      document.addEventListener(EVENTS.SUCCESS, (e) => {
        if (!config.history) return;
        // Live task — needs pillowData (not on event imageTaskSlim)
        const imageTask = Pipeline.getImageTask();
        if (!imageTask?.s3Url) return;

        const props = imageTask.props || {};
        adapters.history.createHistoryItem({
          id: imageTask.id,
          fileOriginalId: imageTask.fileOriginalId,
          props,
          color: props.color || null,
          s3Url: imageTask.s3Url,
          originalS3Url: imageTask.originalS3Url || null,
          pillowData: imageTask.pillowData,
          preview: imageTask.preview,
          previewWithBg: imageTask.previewWithBg || imageTask.preview || null,
          productType: config.productType || null
        });
      });
    },

    /** Thin bind → Pipeline inbound handlers. Upload → wiring. */
    bindExternalEvents: () => {
      const eventDetail = (e, jqueryData) => (
        jqueryData
        || e?.originalEvent?.detail
        || e?.detail
        || {}
      );

      $(document).on('mockup:start', (e) => {
        Pipeline.onMockupStart(eventDetail(e));
      });

      $(document).on('mockup:success', (e) => {
        Pipeline.onMockupSuccess(eventDetail(e));
      });

      $(document).on('mockup:error', (e) => {
        Pipeline.onMockupError(eventDetail(e));
      });

      $(document).on('saveToS3:success', (e, eventData) => {
        Pipeline.onS3Success(eventDetail(e, eventData));
      });

      $(document).on('saveToS3:error', (e, errorData) => {
        Pipeline.onS3Error(eventDetail(e, errorData));
      });

      $(document).on('productFormCustom:propChange', (e) => {
        Pipeline.onPropChange(eventDetail(e));
      });

      // History UI click → restore model (size mockups listen to same event separately)
      document.addEventListener('mockupHistory:loadFile', (e) => {
        if (!config?.history) return;
        const detail = e.detail || {};
        const s3Url = detail.s3Url || null;
        const pillowData = detail.pillowData || null;
        if (!s3Url && !pillowData) return;

        let item = null;
        if (detail.fileId && window.FileHistoryManager?.getFileFromHistory) {
          item = window.FileHistoryManager.getFileFromHistory(detail.fileId);
        }

        const props = {
          ...adapters.form.getMockupProps(),
          ...(item?.props && typeof item.props === 'object' ? item.props : {}),
          ...(detail.props && typeof detail.props === 'object' ? detail.props : {}),
          ...(detail.color ? { color: detail.color } : {})
        };

        Pipeline.startFromHistory({
          props,
          engine: { pillowData },
          save: { final: s3Url },
          fileId: detail.fileId || null,
          fileOriginalId: detail.fileOriginalId || item?.fileOriginalId || null
        });
      });
    }
  };

  const initializeEventHandlers = () => {
    EventService.setupInternalHandlers();
    EventService.bindExternalEvents();
  };

  // ═══════════════════════════════════════════════════════════
  // 8) BOOT + PUBLIC API
  // ═══════════════════════════════════════════════════════════

  /**
   * Init orchestrator. Product boots pass config + getEngine (or engine).
   * If getEngine() is not ready yet (deferred engine script), polls briefly
   * then inits — boots do not need their own wait loops.
   * Optional opts.onReady() runs after successful init (and if already inited).
   */
  let bootWaitTimer = null;

  const finishBoot = (opts, engine) => {
    const { getEngine: _getEngine, engine: _engineOpt, onReady, ...bootOpts } = opts;
    Pipeline.init({
      ...bootOpts,
      engine
    });

    initializeEventHandlers();

    window.customProductOrchestratorInitialized = true;
    bootWaitTimer = null;

    EventService.dispatch(EVENTS.READY, {
      config: Pipeline.getConfig()
    });
    $(document).trigger('custom-product-orchestrator:ready');

    if (typeof onReady === 'function') onReady();
  };

  const boot = (opts = {}) => {
    if (window.customProductOrchestratorInitialized) {
      if (typeof opts.onReady === 'function') opts.onReady();
      return;
    }

    const resolveEngine = () => (
      typeof opts.getEngine === 'function' ? opts.getEngine() : opts.engine
    );

    const engineNow = resolveEngine();
    if (engineNow) {
      if (bootWaitTimer) {
        clearInterval(bootWaitTimer);
        bootWaitTimer = null;
      }
      finishBoot(opts, engineNow);
      return;
    }

    if (typeof opts.getEngine !== 'function') {
      throw new Error('boot: engine is required (pass engine or getEngine)');
    }

    // Already waiting on a previous boot() call
    if (bootWaitTimer) return;

    let tries = 0;
    bootWaitTimer = setInterval(() => {
      tries += 1;
      const engine = resolveEngine();
      if (engine) {
        clearInterval(bootWaitTimer);
        finishBoot(opts, engine);
        return;
      }
      if (tries > 200) {
        clearInterval(bootWaitTimer);
        bootWaitTimer = null;
        console.error(
          '[custom-product-orchestrator-v2] boot: engine not ready after timeout — load engine script before/with boot'
        );
      }
    }, 25);
  };

  // Facade — commands + helpers; propChange / pipeline replies stay internal.
  window.CustomProductOrchestrator = {
    boot,
    getConfig: () => Pipeline.getConfig(),
    getImageTask: () => Pipeline.getImageTask(),
    getMockupProps: () => adapters.form.getMockupProps(),
    matchesUploadTarget,
    startFromUpload: (opts) => Pipeline.startFromUpload(opts),
    startFromHistory: (opts) => Pipeline.startFromHistory(opts),
    startFromShare: (opts) => Pipeline.startFromShare(opts),
    startFromProps: (opts) => Pipeline.startFromProps(opts),
    initialState: () => Pipeline.initialState()
  };
})();

